import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Settings } from "react-feather";
import { usePresence } from "../../../hooks/usePresence";
import { POPOVER_EXIT_MS } from "../../../design-system/tokens/popoverMotion";
import { useSelectedWorkspace } from "../../../hooks/useSelectedWorkspace";
import {
  Caret,
  CheckSlot,
  EmptyState,
  ManageRow,
  Menu,
  MenuDivider,
  Trigger,
  TriggerBadge,
  TriggerValue,
  Wrapper,
  WorkspaceRow,
  WorkspaceRowName,
} from "./WorkspaceSelectorDropdown.styles";
import type { WorkspaceSelectorDropdownProps } from "./WorkspaceSelectorDropdown.types";

const DEFAULT_FALLBACK = "Select workspace";

/**
 * Top-bar workspace switcher. Closed: "[initial] <name>  ⌄". Open: list of
 * workspaces with a checkmark on the current selection, divider, and a
 * "Manage Workspace" footer row that hands off to the host.
 *
 * Selection is URL-driven via `useSelectedWorkspace`. Components elsewhere
 * (e.g. WorkspaceCard's `isSelected`) see the new value automatically.
 *
 * Classified as an ORGANISM because it owns open/closed UI state and the
 * outside-click + Escape listeners.
 */
export const WorkspaceSelectorDropdown: React.FC<
  WorkspaceSelectorDropdownProps
> = ({
  workspaces,
  fallbackLabel = DEFAULT_FALLBACK,
  onManageWorkspace,
  onWorkspaceChange,
}) => {
  const { selectedId, setSelectedId } = useSelectedWorkspace();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { mounted } = usePresence(open, POPOVER_EXIT_MS);

  const close = useCallback(() => setOpen(false), []);

  // Click outside closes.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, close]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, close]);

  const selectedWorkspace = useMemo(
    () => workspaces.find((w) => w.id === selectedId) ?? null,
    [workspaces, selectedId],
  );

  const triggerLabel = selectedWorkspace?.name ?? fallbackLabel;

  const handleRowClick = (id: string) => () => {
    const workspace = workspaces.find((item) => item.id === id);
    if (workspace) onWorkspaceChange?.(workspace);
    setSelectedId(id);
    close();
  };

  const handleManage = () => {
    onManageWorkspace();
    close();
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Workspace: ${triggerLabel}`}
        onClick={() => setOpen((v) => !v)}
      >
        {selectedWorkspace ? (
          <TriggerBadge aria-hidden="true">{triggerLabel.charAt(0)}</TriggerBadge>
        ) : null}
        <TriggerValue title={triggerLabel}>{triggerLabel}</TriggerValue>
        <Caret aria-hidden="true" $open={open}>
          <ChevronDown />
        </Caret>
      </Trigger>

      {mounted && (
        <Menu role="menu" $closing={!open}>
          {workspaces.length === 0 ? (
            <EmptyState>No workspaces available</EmptyState>
          ) : (
            workspaces.map((w) => {
              const isSelected = w.id === selectedId;
              return (
                <WorkspaceRow
                  key={w.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isSelected}
                  $isSelected={isSelected}
                  onClick={handleRowClick(w.id)}
                >
                  <WorkspaceRowName title={w.name}>{w.name}</WorkspaceRowName>
                  {isSelected && (
                    <CheckSlot aria-hidden="true">
                      <Check />
                    </CheckSlot>
                  )}
                </WorkspaceRow>
              );
            })
          )}

          <MenuDivider role="separator" />

          <ManageRow
            type="button"
            role="menuitem"
            onClick={handleManage}
          >
            <Settings size={16} aria-hidden="true" />
            Manage Workspace
          </ManageRow>
        </Menu>
      )}
    </Wrapper>
  );
};

export default WorkspaceSelectorDropdown;
